import { useSelector, useDispatch } from 'react-redux';
import { differenceInMinutes, differenceInSeconds, format } from 'date-fns';
import { useMemo, useCallback } from 'react';
import { SESSION_TYPES, CLIENT_TYPES } from '@medflex/utils/constants';
import { SessionProviders } from '@medflex/shared-constants';
import config from '@medflex/utils/config';
import isPastMeeting from '@medflex/utils/isPastMeeting';
import useUserProfile from './useUserProfile';
import useAddressBookContacts from './useAddressBookContacts';
import { TYPE } from '../store/models/sessionMeetings';

const { SESSIONS_URL } = config;

const getDurationString = (startDate, endDate) => {
  const minutes = Math.max(0, differenceInMinutes(new Date(endDate), new Date(startDate)));
  const seconds = Math.max(0, differenceInSeconds(new Date(endDate), new Date(startDate)) % 60);
  return `${minutes}:${seconds > 9 ? seconds : `0${seconds}`} Min`;
};

const useSessionMeetings = () => {
  const { meetings = [] } = useSelector(state => state[TYPE]);
  const { userProfile } = useUserProfile();
  const { sessions: errors = {} } = useSelector(state => state.errors);
  const isLoading = useSelector(state => state.loading.models[TYPE]);
  const isResendingInvitation = useSelector(
    state => state.loading.effects.sessions.resendInvitation,
  );

  const dispatch = useDispatch();
  const { all } = useAddressBookContacts();

  const create = data => {
    dispatch.sessions.create({ ...data, type: SESSION_TYPES.MEETING });
  };

  const cancel = ({ id }) => {
    const meeting = meetings.find(s => s.id === id);
    if (meeting) {
      const { tan, participantId } = meeting.participants.find(p => p.userId === userProfile.id);
      dispatch.sessions.update({
        id,
        tan,
        participantId,
        attributes: { scheduleCanceledAt: new Date() },
      });
    }
  };

  const resend = sessionId => dispatch.sessions.resendInvitation(sessionId);

  const load = () => {
    dispatch[TYPE].load();
  };

  const find = (id, participantId, tan) => {
    dispatch.sessions.find({ id, participantId, tan });
  };

  const openInSameTab = async ({ id, participantId, callJoinUrl }) => {
    const callUrl = new URL(callJoinUrl);

    const otp = await dispatch.sessions.getOneTimePassword({
      sessionId: id,
      participantId,
    });
    callUrl.searchParams.set('tid', otp);

    const link = document.createElement('a');
    link.href = callUrl.toString();
    link.click();
  };

  const openInNewTabDeferred = async ({ id, participantId, callJoinUrl }) => {
    const callUrl = new URL(callJoinUrl);

    const newTab = window.open('', { target: '_blank' });

    const otp = await dispatch.sessions.getOneTimePassword({
      sessionId: id,
      participantId,
    });

    callUrl.searchParams.set('tid', otp);
    newTab.location.href = callUrl.toString();
  };

  const openInNewTab = async ({ id, participantId, callJoinUrl }) => {
    const callUrl = new URL(callJoinUrl);

    const otp = await dispatch.sessions.getOneTimePassword({
      sessionId: id,
      participantId,
    });

    callUrl.searchParams.set('tid', otp);
    window.open(callUrl.toString(), { target: '_blank' });
  };

  const goTo = async ({ id }) => {
    const meeting = meetings.find(s => s.id === id);
    if (meeting) {
      const { tan, participantId, callJoinUrl } = meeting.participants.find(
        p => p.userId === userProfile.id,
      );
      if (meeting.provider === SessionProviders.ARZT_KONSULTATION) {
        const callUrl = new URL(callJoinUrl);

        if (callUrl.searchParams.has('tan')) {
          window.open(callUrl, { target: '_blank' });
          return;
        }

        if (config.CLIENT_TYPE === CLIENT_TYPES.MOBILE) {
          openInSameTab({
            id,
            participantId,
            callJoinUrl,
          });

          return;
        }

        if (config.CLIENT_TYPE === CLIENT_TYPES.DESKTOP) {
          openInNewTab({
            id,
            participantId,
            callJoinUrl,
          });

          return;
        }

        openInNewTabDeferred({
          id,
          participantId,
          callJoinUrl,
        });

        return;
      }
      window.open(
        `${SESSIONS_URL}/${id}/${participantId}?tan=${tan}&settings=${
          meeting.establishedAt ? 0 : 1
        }`,
        {
          target: '_blank',
        },
      );
    }
  };

  const getAttendeeInfoFromParticipantList = useCallback(
    participants => {
      if (participants.length > 2) return null;
      const attendee = participants.find(p => p.userId !== userProfile.id);
      const attendeeContact =
        attendee.userId && all.find(({ user }) => user && user.id === attendee.userId);

      return {
        attendee: {
          avatar: attendeeContact && attendeeContact.user.avatar,
          initials: attendeeContact && attendeeContact.user.initials,
        },
      };
    },
    [all, userProfile.id],
  );

  const pastMeetings = useMemo(
    () =>
      meetings.filter(isPastMeeting).map(meeting => ({
        ...meeting,
        ...getAttendeeInfoFromParticipantList(meeting.participants),
        duration: getDurationString(
          meeting.establishedAt || meeting.scheduledAt,
          meeting.lastTouchedAt || meeting.scheduledAt,
        ),
      })),
    [meetings, getAttendeeInfoFromParticipantList],
  );

  const futureMeetings = useMemo(
    () =>
      meetings
        .filter(
          meeting =>
            !meeting.scheduleCanceledAt && !pastMeetings.map(({ id }) => id).includes(meeting.id),
        )
        .map(meeting => ({
          ...meeting,
          ...getAttendeeInfoFromParticipantList(meeting.participants),
        })),
    [meetings, pastMeetings],
  );

  const getMeetingById = sessionId =>
    useMemo(() => meetings.find(({ id }) => id === sessionId), [meetings, sessionId]);

  const getSessionById = sessionId =>
    useMemo(() => meetings.find(({ id }) => id === sessionId), [meetings, sessionId]);

  const getDateFormatted = (isoString, dateFormat = 'DD.MM.YYYY [um] HH:mm') =>
    useMemo(() => format(isoString, dateFormat), [isoString, dateFormat]);

  return {
    create,
    find,
    load,
    cancel,
    resend,
    meetings,
    errors,
    isLoading,
    isResendingInvitation,
    pastMeetings,
    futureMeetings,
    goTo,
    update: dispatch.sessions.authUpdate,
    addParticipant: dispatch.sessions.addParticipant,
    removeParticipant: dispatch.sessions.removeParticipant,
    changeModerator: dispatch.sessions.changeModerator,
    getMeetingById,
    getSessionById,
    getDateFormatted,
    getOneTimePassword: dispatch.sessions.getOneTimePassword,
  };
};

export default useSessionMeetings;
